/**
 * ScheduleTourModal — bespoke studio UI for the `scheduleTourModal` corpus
 * modal id (openModal), from ecommerce/property-detail.
 *
 * Forwarded props (`popup` stripped): { propertyId }.
 *
 * Preview behavior: no backend in the build-mode canvas, so Request fires a
 * success toast and closes (parity with use-studio-host-services). Bound to its
 * modal id by createStudioModalRegistry.
 */
import { type FormEvent } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import type { ModalComponentProps } from '@cogo/react-renderer-v2'
import { Button } from '@/components/ui/button'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { toast } from '@/store/toast-store'

export function ScheduleTourModal({ modalId, props, close }: ModalComponentProps & { modalId: string }) {
  const propertyId = typeof props.propertyId === 'string' ? props.propertyId : undefined

  const submit = (e: FormEvent) => {
    e.preventDefault()
    toast.success('Tour requested')
    close()
  }

  return (
    <Dialog.Root open onOpenChange={(open) => !open && close()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <Dialog.Content
          data-testid="schedule-tour-modal"
          data-modal-id={modalId}
          className="fixed left-1/2 top-1/2 z-50 w-full max-w-[440px] -translate-x-1/2 -translate-y-1/2 rounded-xl border border-border bg-card p-6 text-foreground shadow-xl"
        >
          <Dialog.Title className="mb-1 text-lg font-semibold">Schedule a tour</Dialog.Title>
          <Dialog.Description className="mb-4 text-sm text-muted-foreground">
            Pick a date and time to tour this property.
          </Dialog.Description>
          {propertyId ? (
            <p className="mb-4 text-xs text-muted-foreground">
              Property: <span className="font-mono text-foreground">{propertyId}</span>
            </p>
          ) : null}

          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Date">
                <Input name="date" type="date" aria-label="Tour date" />
              </Field>
              <Field label="Time">
                <Input name="time" type="time" aria-label="Tour time" />
              </Field>
            </div>
            <Field label="Name">
              <Input name="name" placeholder="Your name" autoComplete="name" />
            </Field>
            <Field label="Email">
              <Input name="email" type="email" placeholder="you@email.com" autoComplete="email" />
            </Field>
            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="outline" onClick={close}>
                Cancel
              </Button>
              <Button type="submit">Request tour</Button>
            </div>
          </form>

          <Dialog.Close asChild>
            <button
              type="button"
              aria-label="Close"
              className="absolute right-3 top-3 rounded-sm opacity-70 transition-opacity hover:opacity-100"
            >
              <X className="h-4 w-4" />
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
